import { createFeatureSelector, createSelector } from '@ngrx/store';
import { BusinessRole } from '../../shared/models/role';
import { BulkImportState, RegViewConfigCacheEntry, RequestState, ResourceKey, State } from '../reducers/com.reducer';

export const COM_FEATURE_KEY = 'com';

export const selectCOMFeature = createFeatureSelector<State>(COM_FEATURE_KEY);

// Generic request selectors
export const selectRequest = (key: ResourceKey) => createSelector(selectCOMFeature, (state: State) => state.requests[key]);

export const selectLoading = (key: ResourceKey) => createSelector(selectRequest(key), (r: RequestState) => r.loading);

export const selectLoaded = (key: ResourceKey) => createSelector(selectRequest(key), (r: RequestState) => r.loaded);

export const selectError = (key: ResourceKey) => createSelector(selectRequest(key), (r: RequestState) => r.error);

// User Selectors
export const selectUser = createSelector(selectCOMFeature, (state: State) => state.user);
export const selectIsAdmin = createSelector(selectUser, (user) => user?.profile?.adminGroup?.['status'] === 'ACTIVE');

// Roles Selectors
export const selectRoles = createSelector(selectCOMFeature, (state: State) => state.roles);
export const selectBusinessRoles = createSelector(selectRoles, (roles) => roles.map((r) => r.businessRole));
export const selectHasBusinessRole = (role: BusinessRole) => createSelector(selectRoles, (roles) => roles.some((r) => r.businessRole === role));

// Programs selectors
export const selectAllPrograms = createSelector(selectCOMFeature, (state: State) => state.programs);

export const selectSelectedProgram = createSelector(selectCOMFeature, (state: State) => state.selectedProgram);

export const selectProgramsRequest = createSelector(selectCOMFeature, (state: State) => state.requests.programs);

export const selectProgramsLoading = createSelector(selectProgramsRequest, (request: RequestState) => request.loading);

export const selectProgramsLoaded = createSelector(selectProgramsRequest, (request: RequestState) => request.loaded);

export const selectProgramsError = createSelector(selectProgramsRequest, (request: RequestState) => request.error);

// Registrations selectors
export const selectAllRegistrations = createSelector(selectCOMFeature, (state: State) => state.registrations);

export const selectSelectedRegistration = createSelector(selectCOMFeature, (state: State) => state.selectedRegistration);

export const selectRegistrationsRequest = createSelector(selectCOMFeature, (state: State) => state.requests.registrations);

export const selectRegistrationsLoading = createSelector(selectRegistrationsRequest, (request: RequestState) => request.loading);

export const selectRegistrationsLoaded = createSelector(selectRegistrationsRequest, (request: RequestState) => request.loaded);

export const selectRegistrationsError = createSelector(selectRegistrationsRequest, (request: RequestState) => request.error);

// RegViewConfig
export const selectRegViewConfigCache = createSelector(selectCOMFeature, (state: State) => state.regViewConfigCache);

export const selectCurrentRegViewConfig = createSelector(selectCOMFeature, (state: State) => state.currentRegViewConfig);

export const selectCurrentRegViewConfigId = createSelector(selectCOMFeature, (state: State) => state.currentRegViewConfigId);

export const selectRegViewConfigRequest = createSelector(selectCOMFeature, (state: State) => state.requests.regViewConfig);

export const selectRegViewConfigLoading = createSelector(selectRegViewConfigRequest, (request: RequestState) => request.loading);

export const selectRegViewConfigLoaded = createSelector(selectRegViewConfigRequest, (request: RequestState) => request.loaded);

export const selectRegViewConfigError = createSelector(selectRegViewConfigRequest, (request: RequestState) => request.error);

// RegViewConfig: Get a specific RegviewConfig from cache by registrationId
export const selectIsRegviewConfigCached = (registrationId: string) =>
  createSelector(selectRegViewConfigCache, (cache: Record<string, RegViewConfigCacheEntry>) => !!cache[registrationId]);

// Check if a specific RegViewConfig is cached
export const selectIsRegViewConfigCached = (registrationId: string) =>
  createSelector(selectRegViewConfigCache, (cache: Record<string, RegViewConfigCacheEntry>) => !!cache[registrationId]);

// Get all cached registration IDs
export const selectCachedRegistrationIds = createSelector(selectRegViewConfigCache, (cache: Record<string, RegViewConfigCacheEntry>) =>
  Object.keys(cache)
);

// ============================================
// Graph Offers Selectors
// ============================================
export const selectCurrentGraphOffers = createSelector(selectCOMFeature, (state: State) => state.graphOffers);

export const selectGraphOffersRequest = createSelector(selectCOMFeature, (state: State) => state.requests.graphOffers);

export const selectGraphOffersLoading = createSelector(selectGraphOffersRequest, (request: RequestState) => request.loading);

export const selectGraphOffersLoaded = createSelector(selectGraphOffersRequest, (request: RequestState) => request.loaded);

export const selectGraphOffersError = createSelector(selectGraphOffersRequest, (request: RequestState) => request.error);

// ============================================
// Open Offers Selectors
// ============================================

export const selectCurrentOpenOffers = createSelector(selectCOMFeature, (state: State) => state.currentOpenOffers);

export const selectOpenOffersRequest = createSelector(selectCOMFeature, (state: State) => state.requests.openOffers);

export const selectOpenOffersLoading = createSelector(selectOpenOffersRequest, (request: RequestState) => request.loading);

export const selectOpenOffersLoaded = createSelector(selectOpenOffersRequest, (request: RequestState) => request.loaded);

export const selectOpenOffersError = createSelector(selectOpenOffersRequest, (request: RequestState) => request.error);

// ============================================
// Cleared Offers Selectors
// ============================================

export const selectCurrentClearedOffers = createSelector(selectCOMFeature, (state: State) => state.currentClearedOffers);

export const selectClearedOffersRequest = createSelector(selectCOMFeature, (state: State) => state.requests.clearedOffers);

export const selectClearedOffersLoading = createSelector(selectClearedOffersRequest, (request: RequestState) => request.loading);

export const selectClearedOffersLoaded = createSelector(selectClearedOffersRequest, (request: RequestState) => request.loaded);

export const selectClearedOffersError = createSelector(selectClearedOffersRequest, (request: RequestState) => request.error);

// ============================================
// Cleared Graph Offers Selectors
// ============================================

export const selectCurrentClearedGraphOffers = createSelector(selectCOMFeature, (state: State) => state.clearedGraphOffers);

export const selectClearedGraphOffersRequest = createSelector(selectCOMFeature, (state: State) => state.requests.clearedGraphOffers);

export const selectClearedGraphOffersLoading = createSelector(selectClearedGraphOffersRequest, (request: RequestState) => request.loading);

export const selectClearedGraphOffersLoaded = createSelector(selectClearedGraphOffersRequest, (request: RequestState) => request.loaded);

export const selectClearedGraphOffersError = createSelector(selectClearedGraphOffersRequest, (request: RequestState) => request.error);

// ============================================
// Scheduled Offers Selectors
// ============================================

export const selectCurrentScheduledOffers = createSelector(selectCOMFeature, (state: State) => state.currentScheduledOffers);

export const selectScheduledOffersRequest = createSelector(selectCOMFeature, (state: State) => state.requests.scheduledOffers);

export const selectScheduledOffersLoading = createSelector(selectScheduledOffersRequest, (request: RequestState) => request.loading);

export const selectScheduledOffersLoaded = createSelector(selectScheduledOffersRequest, (request: RequestState) => request.loaded);

export const selectScheduledOffersError = createSelector(selectScheduledOffersRequest, (request: RequestState) => request.error);

// ============================================
// Baseline Offers Selectors
// ============================================

export const selectCurrentBaselineOffers = createSelector(selectCOMFeature, (state: State) => state.currentBaselineOffers);

export const selectBaselineOffersRequest = createSelector(selectCOMFeature, (state: State) => state.requests.baselineOffers);

export const selectBaselineOffersLoading = createSelector(selectBaselineOffersRequest, (request: RequestState) => request.loading);

export const selectBaselineOffersLoaded = createSelector(selectBaselineOffersRequest, (request: RequestState) => request.loaded);

export const selectBaselineOffersError = createSelector(selectBaselineOffersRequest, (request: RequestState) => request.error);

// ============================================
// DA Offers Selectors
// ============================================

export const selectCurrentDAOffers = createSelector(selectCOMFeature, (state: State) => state.currentDAOffers);

export const selectDAOffersRequest = createSelector(selectCOMFeature, (state: State) => state.requests.daOffers);

export const selectDAOffersLoading = createSelector(selectDAOffersRequest, (request: RequestState) => request.loading);

export const selectDAOffersLoaded = createSelector(selectDAOffersRequest, (request: RequestState) => request.loaded);

export const selectDAOffersError = createSelector(selectDAOffersRequest, (request: RequestState) => request.error);

// ============================================
// Bulk Import Selectors
// ============================================

export const selectBulkImport = createSelector(selectCOMFeature, (state: State) => state.bulkImport);

export const selectBulkImportUploadStatus = createSelector(selectBulkImport, (s: BulkImportState) => s.uploadStatus);

export const selectBulkImportUploadProgress = createSelector(selectBulkImport, (s: BulkImportState) => s.uploadProgress);

export const selectBulkImportLastStatus = createSelector(selectBulkImport, (s: BulkImportState) => s.lastImportStatus);

export const selectBulkImportImportType = createSelector(selectBulkImport, (s: BulkImportState) => s.importType);

export const selectBulkImportImportId = createSelector(selectBulkImport, (s: BulkImportState) => s.importId);

export const selectBulkImportRegistrationId = createSelector(selectBulkImport, (s: BulkImportState) => s.registrationId);

export const selectBulkImportPercent = createSelector(selectBulkImport, (s: BulkImportState) => s.percent);
